import { useState } from 'react'
import { formatCurrency } from '../../lib/format'

interface Variant {
  id: number
  sku: string | null
  price: number | null
  stock_quantity: number
  option_display: string
}

interface Product {
  id: number
  name: string
  price: number
  track_inventory: boolean
  variants: Variant[]
}

interface Props {
  product: Product
  /**
   * Se llama cuando el usuario confirma la selección.
   * `quantity` empieza en 1; el usuario puede subirla con el stepper del modal.
   * Si `quantity > 1`, el backend hace upsert (suma a la línea existente).
   */
  onAdd: (variant: Variant, quantity: number) => void
  onClose: () => void
}

export default function VariantModal({ product, onAdd, onClose }: Props) {
  const [quantity, setQuantity] = useState(1)
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null)

  const availableVariants = product.variants.filter(
    (v) => !product.track_inventory || v.stock_quantity > 0
  )

  const handleConfirm = () => {
    if (!selectedVariant) return
    onAdd(selectedVariant, quantity)
  }

  // Display price for the footer. If a variant is selected, use its price
  // (falling back to the product price if the variant has no override).
  // If no variant is selected yet, show the product's base price.
  const unitPrice = selectedVariant
    ? (selectedVariant.price ?? product.price)
    : product.price
  const lineTotal = unitPrice * quantity
  const confirmLabel = selectedVariant
    ? `Agregar al carrito · ${formatCurrency(lineTotal)}`
    : 'Selecciona una variante'

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header — fixed at top */}
        <div className="flex justify-between items-start p-6 pb-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#202020]">{product.name}</h3>
            <p className="text-sm text-gray-500">Selecciona una variante</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2"
          >
            ×
          </button>
        </div>

        {/* Body — scrollable, takes remaining space */}
        <div className="flex-1 overflow-y-auto p-6">
          {availableVariants.length === 0 ? (
            <p className="text-center text-gray-400 py-8">
              No hay variantes disponibles
            </p>
          ) : (
            <div className="space-y-2">
              {availableVariants.map((variant) => {
                const isSelected = selectedVariant?.id === variant.id
                return (
                  <button
                    key={variant.id}
                    onClick={() => {
                      setSelectedVariant(variant)
                      setQuantity(1)  // reset al cambiar de variante
                    }}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      isSelected
                        ? 'border-[#55ACEE] bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-[#202020]">
                        {variant.option_display}
                      </span>
                      <span className="font-bold text-[#55ACEE] text-lg">
                        {formatCurrency(variant.price ?? product.price)}
                      </span>
                    </div>
                    {product.track_inventory && (
                      <p className="text-xs text-gray-400 mt-1">
                        Stock: {variant.stock_quantity}
                      </p>
                    )}
                    {variant.sku && (
                      <p className="text-xs text-gray-400 mt-0.5">SKU: {variant.sku}</p>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer — always visible, sticky at bottom */}
        <div className="border-t border-gray-200 p-6 pt-4 space-y-3 shrink-0 bg-white">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-[#202020]">Cantidad</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                disabled={quantity <= 1}
                className="w-8 h-8 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Disminuir"
              >−</button>
              <span className="w-10 text-center text-base font-semibold tabular-nums">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => {
                  const max = selectedVariant && product.track_inventory
                    ? selectedVariant.stock_quantity
                    : Infinity
                  setQuantity((q) => Math.min(max, q + 1))
                }}
                disabled={
                  !selectedVariant ||
                  (product.track_inventory &&
                    selectedVariant.stock_quantity <= quantity)
                }
                className="w-8 h-8 rounded-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Aumentar"
              >+</button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedVariant}
            className="w-full bg-[#FFC107] text-[#202020] py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
